/**
 * ⌘K command palette.
 *
 * Two modes:
 *  - **browse**: fuzzy-search workspaces, projects, jump-to settings, and
 *    a "Create task…" entry. Default mode when the palette opens.
 *  - **compose**: when the user picks "Create task…" (only shown when we're
 *    inside a project route, i.e. `/w/:wsId/p/:pKey`). The palette becomes a
 *    mini create-task form: title input, column select, priority select,
 *    Enter submits. The created task lands in the project's first column
 *    by default (the user can swap to any of the project's columns before
 *    submitting). Esc returns to browse mode.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { api } from '@/lib/api'
import { toast } from '@/features/feedback/toast'
import { useTaskCreatedToast } from '@/features/projects/taskCreatedToast'
import { cn } from '@/lib/utils'
import type { Project, ProjectColumn, Task } from '@friday/shared/realtime'

type Mode = 'browse' | 'compose'

interface CommandPaletteProps {
  onClose: () => void
}

interface Command {
  id: string
  label: string
  hint?: string
  run: () => void
  group: 'Navigate' | 'View' | 'Action'
}

const PRIORITY_OPTIONS = ['medium', 'high', 'urgent', 'low'] as const

export function CommandPalette({ onClose }: CommandPaletteProps) {
  const [mode, setMode] = useState<Mode>('browse')
  // Reset to browse every time the palette re-opens (driven by mount).
  useEffect(() => {
    setMode('browse')
  }, [])

  if (mode === 'compose') {
    return <ComposeMode onClose={onClose} onBack={() => setMode('browse')} />
  }
  return <BrowseMode onClose={onClose} onCompose={() => setMode('compose')} />
}

/** Default browse experience — navigation commands. */
function BrowseMode({ onClose, onCompose }: { onClose: () => void; onCompose: () => void }) {
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const navigate = useNavigate()
  // Strict-false so we can pull whatever segment is present at the time.
  const params = useParams({ strict: false }) as { wsId?: string; pKey?: string }

  const { data: workspaces = [] } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api<Workspace[]>('/api/v1/workspaces'),
  })
  const wsId = params.wsId ?? workspaces[0]?.id
  const { data: projects = [] } = useQuery({
    queryKey: ['workspaces', wsId, 'projects'],
    queryFn: () => api<Project[]>(`/api/v1/workspaces/${wsId}/projects`),
    enabled: !!wsId,
  })

  // Can we offer a Compose-task entry? Need a wsId; the pKey comes from the
  // URL when present, otherwise we fall back to whichever project the user
  // last touched (first in the list is good enough — it's what the sidebar
  // would have shown).
  const activePKey = params.pKey ?? projects[0]?.key ?? null
  const canCompose = !!wsId && !!activePKey

  const commands: Command[] = useMemo(() => {
    const out: Command[] = []
    for (const w of workspaces) {
      out.push({
        id: `w:${w.id}`,
        label: `Go to ${w.name}`,
        group: 'Navigate',
        run: () => navigate({ to: '/w/$wsId', params: { wsId: w.id } }),
      })
    }
    for (const p of projects) {
      if (p.workspaceId !== wsId) continue
      out.push({
        id: `p:${p.id}`,
        label: `${p.key} — ${p.name}`,
        group: 'Navigate',
        run: () =>
          navigate({ to: '/w/$wsId/p/$pKey', params: { wsId: wsId!, pKey: p.key } }),
      })
    }
    out.push({ id: 'settings', label: 'Settings', group: 'Navigate', run: () => navigate({ to: '/settings' }) })
    out.push({ id: 'tokens', label: 'API tokens', group: 'Navigate', run: () => navigate({ to: '/settings/tokens' }) })
    if (canCompose) {
      out.push({
        id: 'compose',
        label: 'Create task…',
        hint: 'in this project',
        group: 'Action',
        run: onCompose,
      })
    }
    return out
  }, [workspaces, projects, wsId, navigate, canCompose, onCompose])

  const filtered = useMemo(() => {
    if (!q.trim()) return commands
    const needle = q.toLowerCase()
    return commands.filter((c) => c.label.toLowerCase().includes(needle))
  }, [q, commands])

  useEffect(() => setActive(0), [q])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActive((a) => Math.min(filtered.length - 1, a + 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActive((a) => Math.max(0, a - 1))
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        const c = filtered[active]
        if (c) {
          c.run()
        }
        return
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [filtered, active, onClose])

  const grouped = useMemo(() => {
    const out = new Map<string, Command[]>()
    for (const c of filtered) {
      const arr = out.get(c.group) ?? []
      arr.push(c)
      out.set(c.group, arr)
    }
    return out
  }, [filtered])

  return (
    <PaletteShell onClose={onClose}>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Jump to project, switch view…"
        className="w-full border-b border-surface-200 dark:border-surface-700 bg-transparent px-4 py-3 text-sm placeholder:text-surface-500 focus:outline-none"
      />
      <div className="max-h-80 overflow-y-auto p-1">
        {filtered.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-surface-500">No results.</div>
        )}
        {Array.from(grouped.entries()).map(([group, items]) => (
          <div key={group} className="py-1">
            <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-surface-500">
              {group}
            </div>
            {items.map((c) => {
              const idx = filtered.indexOf(c)
              return (
                <button
                  key={c.id}
                  onMouseEnter={() => setActive(idx)}
                  onClick={() => c.run()}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm',
                    idx === active
                      ? 'bg-brand-500/10 text-brand-700 dark:text-brand-200'
                      : 'hover:bg-surface-100 dark:hover:bg-surface-800',
                  )}
                >
                  <span className="flex-1">{c.label}</span>
                  {c.hint && <span className="text-[10px] text-surface-500">{c.hint}</span>}
                </button>
              )
            })}
          </div>
        ))}
      </div>
      <PaletteFooter />
    </PaletteShell>
  )
}

/**
 * Compose mode — the palette becomes a quick task creator. Press Enter to
 * submit, Esc to back out to browse mode (without closing the palette).
 */
function ComposeMode({ onClose, onBack }: { onClose: () => void; onBack: () => void }) {
  const params = useParams({ strict: false }) as { wsId?: string; pKey?: string }
  const wsId = params.wsId
  const pKey = params.pKey

  const [title, setTitle] = useState('')
  const [priority, setPriority] = useState<(typeof PRIORITY_OPTIONS)[number]>('medium')
  const [columnId, setColumnId] = useState<string>('')

  // Look up the project for the URL key so we know the pId to POST against.
  const { data: projects = [] } = useQuery({
    queryKey: ['workspaces', wsId, 'projects'],
    queryFn: () => api<Project[]>(`/api/v1/workspaces/${wsId}/projects`),
    enabled: !!wsId,
  })
  const project = useMemo(() => {
    if (!pKey) return projects[0]
    return projects.find((p) => p.key === pKey) ?? projects[0]
  }, [projects, pKey])

  const { data: columns = [], isLoading: colsLoading } = useQuery({
    queryKey: ['project-columns', project?.id],
    queryFn: () => api<ProjectColumn[]>(`/api/v1/projects/${project!.id}/columns`),
    enabled: !!project?.id,
  })

  // Pick a sensible default column once columns load.
  useEffect(() => {
    if (!columns.length) return
    if (!columnId || !columns.find((c) => c.id === columnId)) {
      const todo = columns.find((c) => c.kind === 'todo')
      setColumnId(todo?.id ?? columns[0]!.id)
    }
  }, [columns, columnId])

  const qc = useQueryClient()
  const announceCreated = useTaskCreatedToast(wsId)
  const create = useMutation({
    mutationFn: () =>
      api<Task>(`/api/v1/projects/${project!.id}/tasks`, {
        method: 'POST',
        body: { title: title.trim(), priority, columnId },
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: (task) => {
      void qc.invalidateQueries({ queryKey: ['board', wsId, project!.id] })
      announceCreated(task)
      onClose()
    },
    onError: (e) => {
      toast(e instanceof Error ? e.message : 'Failed to create task', { kind: 'error' })
    },
  })

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Esc backs out unless we're mid-submit; ⌘+K toggles; ⌘+Enter submits.
      if (e.key === 'Escape') {
        e.preventDefault()
        if (!create.isPending) onBack()
        return
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onBack, create.isPending])

  const canSubmit = !!title.trim() && !!project?.id && !!columnId && !create.isPending

  // Form submit on Enter — render an actual <form> so the input's native
  // Enter → submit behaviour works without us re-implementing it.
  function onSubmit(ev: React.FormEvent) {
    ev.preventDefault()
    if (!canSubmit) return
    create.mutate()
  }

  return (
    <PaletteShell onClose={onClose}>
      <form onSubmit={onSubmit} className="border-b border-surface-200 dark:border-surface-700 px-4 py-3">
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="New task title…"
          className="w-full bg-transparent text-sm placeholder:text-surface-500 focus:outline-none"
        />
      </form>
      <div className="space-y-2 p-3 text-xs">
        <div className="flex items-center gap-2">
          <span className="w-16 text-surface-500">Project</span>
          <span className="font-mono text-[11px] text-surface-500">
            {project ? `${project.key} — ${project.name}` : '—'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-16 text-surface-500">Column</span>
          <select
            value={columnId}
            onChange={(e) => setColumnId(e.target.value)}
            className="flex-1 rounded border border-surface-300 dark:border-surface-700 bg-white dark:bg-surface-800 px-2 py-1"
            disabled={colsLoading || !project?.id}
          >
            {colsLoading ? (
              <option>Loading…</option>
            ) : (
              columns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))
            )}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-16 text-surface-500">Priority</span>
          <div className="flex items-center gap-1">
            {PRIORITY_OPTIONS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPriority(p)}
                className={cn(
                  'rounded-md border border-surface-300 dark:border-surface-700 px-2 py-0.5 capitalize',
                  priority === p
                    ? 'border-brand-500 bg-brand-500/10 text-brand-700 dark:text-brand-200'
                    : 'text-surface-600 hover:bg-surface-100 dark:hover:bg-surface-800',
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
      <PaletteFooter extra={['⏎ create', 'esc back']} />
    </PaletteShell>
  )
}

function PaletteShell({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.1 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[15vh]"
    >
      <motion.div
        initial={{ y: -10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -10, opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl overflow-hidden rounded-xl border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-900 shadow-2xl"
      >
        {children}
      </motion.div>
    </motion.div>
  )
}

function PaletteFooter({ extra }: { extra?: string[] } = {}) {
  return (
    <div className="flex items-center justify-between border-t border-surface-200 dark:border-surface-700 px-3 py-1.5 text-[10px] text-surface-500">
      <div className="flex items-center gap-2">
        <span className="kbd">↑</span><span className="kbd">↓</span> navigate
        <span className="kbd">⏎</span> select
        <span className="kbd">esc</span> close
      </div>
      {extra && extra.length > 0 && (
        <div className="flex items-center gap-2">
          {extra.map((e) => (
            <span key={e} className="kbd">{e}</span>
          ))}
        </div>
      )}
    </div>
  )
}

let palette: { open: boolean; setOpen: (v: boolean) => void } | null = null

export function CommandPaletteMount() {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    palette = { open, setOpen }
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      palette = null
    }
  }, [])

  return (
    <AnimatePresence>
      {open && <CommandPalette onClose={() => setOpen(false)} />}
    </AnimatePresence>
  )
}

export function openCommandPalette() {
  if (palette) palette.setOpen(true)
}

// Re-exported for use in callers that want the same type without importing
// the realtime module directly.
type Workspace = { id: string; name: string }
